import { StopType } from '@brenger/api-client';
import React from 'react';
import { useTypedSelector } from '../../../../../hooks';
import { formatDate } from '../../../../../utils/datetime';
import { translate } from '../../../../../utils/localization';
import {
  DEFAULT_AUCTION_DETAILS,
  getContactAddress,
  getContactPerson,
  getFirstAndLastFlexDates,
  getFlexDatesRange,
  getPriceStructure,
  getSelectedDateTime,
} from '../../../ducks';
import { TermsBlock } from './TermsBlock';
import { StopType as DeprecatedStopType } from '../../../../../types';
import { TermsPrice } from './TermsPrice';

import classNames from 'classnames';
import { Translate } from 'react-localize-redux-dep-updated';
import telephoneIcon from './icon-telephone.svg';
import clipboardIcon from './icon-clipboard-text.svg';
import locationPinIcon from './icon-location-pin.svg';
import calendarIcon from './icon-calendar.svg';
import mapPinIcon from './icon-map-pin.svg';

export const TermsBlockStop = ({ stop }: { stop: StopType }): JSX.Element => {
  const depStop = stop === 'pickup' ? DeprecatedStopType.PICKUP : DeprecatedStopType.DELIVERY;
  const priceStructure = useTypedSelector(getPriceStructure)?.[stop];
  // Address
  const address = useTypedSelector(state => getContactAddress(depStop, state));

  // Date - time
  let dtps = useTypedSelector(state => getSelectedDateTime(state, depStop));
  const flexFirstAndLast = useTypedSelector(getFirstAndLastFlexDates);
  const flexDatesRange = useTypedSelector(getFlexDatesRange);
  if (flexDatesRange.length > 0 && flexFirstAndLast) {
    dtps = flexDatesRange;
  }
  const hasDates = dtps.length > 0;
  const datesAsText = flexFirstAndLast
    ? [
        translate('request_flow.flex_dates.display_range', {
          first_date: formatDate(flexFirstAndLast.first, 'day-month-short'),
          last_date: formatDate(flexFirstAndLast.last, 'day-month-short'),
        }),
      ]
    : dtps.map(dtp => `${formatDate(dtp.start, 'weekday')} ${formatDate(dtp.start, 'day-month-long')}`);
  const dateCharge = priceStructure?.find(item => item.charge === 'date_price');
  const timeCharge = priceStructure?.find(item => item.charge === 'custom_time');

  // Instructions
  const instructions = useTypedSelector(state => state.generalTransport.contact[stop]?.instructions);

  // Contact
  const contact = useTypedSelector(state => getContactPerson(depStop, state));
  const showContact =
    // For auctions we set default details that we don't want to show at this page (because the user did not add them)
    JSON.stringify({
      first_name: contact.details.first_name,
      last_name: contact.details.last_name,
      email: contact.details.email,
      phone: contact.details.phone,
    }) !== JSON.stringify(DEFAULT_AUCTION_DETAILS);

  // File
  const invoices = useTypedSelector(state => state.generalTransport.choices?.invoices);
  const showInvoices = stop === 'pickup' && !!invoices?.length;

  return (
    <div className={'pt-[1em]'}>
      <TermsBlock
        title={<Translate id={`request_flow.terms.block_heading.appointment.${stop}`} />}
        borderRadius="top"
        border="bottom"
        titleClassName="main-title"
      />
      <TermsBlock spacingX={false} spacingY={false} borderRadius="bottom" className="order-overview--split">
        <div
          className={classNames({
            'order-overview--split-left': showContact || showInvoices,
            'w-full': !showContact && !showInvoices,
          })}
        >
          <TermsBlock
            icon={<img src={locationPinIcon} />}
            title={<Translate id={`request_flow.terms.block_heading.address.${stop}`} />}
            borderRadius="none"
            border={hasDates ? 'bottom' : 'none'}
          >
            <div>{address?.line1}</div>
            <div>
              {address?.postal_code} {address?.locality}
            </div>
          </TermsBlock>
          {hasDates && (
            <TermsBlock
              icon={<img src={calendarIcon} />}
              title={<Translate id={`request_flow.terms.block_heading.date_time`} />}
              borderRadius="none"
              border={instructions ? 'bottom' : 'none'}
            >
              <div className="flex justify-between">
                <div>
                  {datesAsText.map((d, i) => (
                    <div key={i} style={{ textTransform: flexFirstAndLast === null ? 'capitalize' : undefined }}>
                      {d}
                    </div>
                  ))}
                </div>
                {dateCharge && <TermsPrice price={dateCharge.price} />}
              </div>

              <div className="flex justify-between">
                <div>{`${formatDate(dtps[0].start, 'hour-minute')} - ${formatDate(dtps[0].end, 'hour-minute')}`}</div>
                {timeCharge && <TermsPrice price={timeCharge.price} />}
              </div>
            </TermsBlock>
          )}
          {instructions && (
            <TermsBlock
              icon={<img src={mapPinIcon} />}
              title={<Translate id={`request_flow.terms.block_heading.instructions`} />}
              borderRadius="none"
            >
              {instructions}
            </TermsBlock>
          )}
        </div>
        {(showContact || showInvoices) && (
          <div className="order-overview--split-right">
            {showContact && (
              <TermsBlock
                icon={<img src={telephoneIcon} />}
                title={<Translate id={`request_flow.terms.block_heading.contact.${stop}`} />}
                border={showInvoices ? 'bottom' : 'none'}
                borderRadius="none"
              >
                <div>
                  {contact.details.first_name} {contact.details.last_name}
                </div>
                <div>{contact.details.email}</div>
                <div>{contact.details.phone}</div>
              </TermsBlock>
            )}
            {showInvoices && (
              <TermsBlock
                icon={<img src={clipboardIcon} />}
                title={<Translate id={`request_flow.terms.block_heading.invoice.pickup`} />}
                borderRadius="none"
              >
                {invoices.map((invoice, i) => (
                  <div key={i}>{invoice.name}</div>
                ))}
              </TermsBlock>
            )}
          </div>
        )}
      </TermsBlock>
    </div>
  );
};
